import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import LaptopChromebookRoundedIcon from '@mui/icons-material/LaptopChromebookRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import { TRICOLOR } from './PageSection';

// Home hero: deep petrol band carrying the brand claim ("Facciamo crescere le
// tue idee") plus the four service entrances of the original qasc.it hero
// rotator — Consulenza, E-Learning, Formazione Sicurezza, Ambiente e Qualità.
const BannerSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#00425c',
  backgroundImage:
    'radial-gradient(circle at 82% 12%, rgba(128,172,192,0.22), transparent 40%), radial-gradient(circle at 10% 24%, rgba(134,163,21,0.20), transparent 40%), radial-gradient(circle at 90% 85%, rgba(193,10,39,0.14), transparent 45%), linear-gradient(165deg, #05537000 0%, rgba(2,49,68,0.55) 100%)',
  overflow: 'hidden',
  padding: '190px 0 100px',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '4px',
    backgroundImage: TRICOLOR,
  },
  [theme.breakpoints.down('md')]: {
    padding: '140px 0 70px',
  },
}));

const Kicker = styled(Typography)(({ theme }) => ({
  color: '#8fc1d6',
  fontFamily: '"Sora", sans-serif',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '3px',
  textTransform: 'uppercase',
  marginBottom: '20px',
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  margin: '0 0 24px',
  maxWidth: '820px',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: '17.5px',
  lineHeight: 1.8,
  fontWeight: 400,
  color: 'rgba(255,255,255,0.82)',
  margin: '0 0 40px',
  maxWidth: '760px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '15.5px',
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#00425c',
  padding: '13px 34px',
  fontSize: '15.5px',
  '&:hover': {
    backgroundColor: '#e9f1f6',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 26px rgba(2,49,68,0.45)',
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  color: '#ffffff',
  border: '1px solid rgba(255,255,255,0.30)',
  padding: '12px 28px',
  fontSize: '15.5px',
  '&:hover': {
    borderColor: '#8fc1d6',
    color: '#8fc1d6',
    backgroundColor: 'transparent',
    transform: 'translateY(-2px)',
  },
}));

// The four entrances of the original homepage hero.
const ENTRANCES = [
  {
    icon: <HealthAndSafetyRoundedIcon />,
    label: 'Formazione Sicurezza',
    to: '/formazione/formazione-sicurezza',
    tint: 'rgba(193,10,39,0.55)',
  },
  {
    icon: <HandshakeRoundedIcon />,
    label: 'Consulenza',
    to: '/consulenza',
    tint: 'rgba(0,66,92,0.85)',
  },
  {
    icon: <LaptopChromebookRoundedIcon />,
    label: 'E-Learning',
    to: '/e-learning',
    tint: 'rgba(128,172,192,0.35)',
  },
  {
    icon: <RecyclingRoundedIcon />,
    label: 'Ambiente e Qualità',
    to: '/formazione/ambiente-e-qualita',
    tint: 'rgba(134,163,21,0.45)',
  },
];

function Banner() {
  return (
    <BannerSection id="rs-banner">
      <Container maxWidth="lg">
        <Kicker>QAS Consulting · Qualità · Ambiente · Sicurezza</Kicker>
        <MainTitle variant="h1">Facciamo crescere le tue idee.</MainTitle>
        <SubTitle>
          QAS Consulting è una realtà di professionisti con sede a Spinea che offre servizi di
          formazione e consulenza aziendale continua. La nostra formula si sintetizza nella
          flessibilità, nella capacità di instaurare solidi rapporti di collaborazione con i
          clienti e nella disponibilità di un affidabile gruppo di professionisti altamente
          qualificati, capaci di erogare un servizio organizzato e coordinato a supporto della
          gestione d’impresa.
        </SubTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: { xs: 6, md: 8 } }}>
          <PrimaryButton variant="contained" component={RouterLink} to="/contatti" disableElevation>
            Contattaci
          </PrimaryButton>
          <GhostButton component={RouterLink} to="/chi-siamo" endIcon={<ArrowForwardRoundedIcon />}>
            Chi siamo
          </GhostButton>
        </Box>

        <Grid container spacing={2.5}>
          {ENTRANCES.map((e) => (
            <Grid item xs={6} md={3} key={e.label}>
              <Box
                component={RouterLink}
                to={e.to}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  height: '100%',
                  padding: '16px 18px',
                  borderRadius: '16px',
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    backgroundColor: 'rgba(255,255,255,0.09)',
                    borderColor: 'rgba(143,193,214,0.45)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: '42px',
                    height: '42px',
                    minWidth: '42px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: e.tint,
                    color: '#ffffff',
                    '& svg': { fontSize: '22px' },
                  }}
                >
                  {e.icon}
                </Box>
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontFamily: '"Sora", sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '13.5px', md: '15px' },
                    lineHeight: 1.35,
                  }}
                >
                  {e.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </BannerSection>
  );
}

export default Banner;
